import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import * as courseSelectors from 'store/courses/selectors';
import { CourseLessonCounts } from 'store/courses/typings';
import { getCourseIdFromProps } from 'utils/selectors/selectors';

import { CourseLessonsData, LessonsData } from '../typings';

import { courseViewSelector } from './base.selectors';

export const getDraftsData = createSelector([courseViewSelector, getCourseIdFromProps], (state, courseId) => {
    if (!courseId) {
        throw Error('no courseId provided');
    }
    return state.drafts[courseId] || ({} as LessonsData);
});

export const getDrafts = createSelector(getDraftsData, (drafts) => drafts.lessons || []);

export const getDraftsLoadingStatus = createSelector(
    [getDraftsData],
    (drafts) => drafts.loadingStatus || LOADING_STATUSES.UNSENT
);

export const getIsDraftsLoading = createSelector(
    [getDraftsLoadingStatus, getDrafts],
    (status, lessons) => status === LOADING_STATUSES.LOADING && lessons.length === 0
);

const getCanLoadMoreDrafts = createSelector(getDraftsData, (drafts) =>
    isEmpty(drafts) ? true : drafts.canLoadMoreLessons
);

export const getShouldLoadMoreDrafts = createSelector(
    [getCanLoadMoreDrafts, getDraftsLoadingStatus],
    (canLoadMoreLessons, loadingStatus) =>
        canLoadMoreLessons && loadingStatus !== LOADING_STATUSES.LOADING && loadingStatus !== LOADING_STATUSES.ERROR
);

export const getDraftsSortKey = createSelector([getDraftsData], (drafts) => drafts.sortKey || null);

export const getCompletedLessonsData = createSelector(
    [courseViewSelector, getCourseIdFromProps],
    (state, courseId) => (courseId && state.completedLessons[courseId]) || ({} as CourseLessonsData)
);

export const getCompletedLessonsLoadingStatus = createSelector(
    [getCompletedLessonsData],
    (data) => data.loadingStatus || LOADING_STATUSES.UNSENT
);

export const getCompletedLessons = createSelector([getCompletedLessonsData], (data) => data.lessons || []);

export const getIsCompletedLessonsLoading = createSelector(
    [getCompletedLessonsLoadingStatus, getCompletedLessons],
    (status, lessons) => status === LOADING_STATUSES.LOADING && lessons.length === 0
);

const getCanLoadCompletedLessons = createSelector([getCompletedLessonsData], (data) =>
    isEmpty(data) ? true : data.canLoadMoreLessons
);

export const getShouldLoadCompletedLessons = createSelector(
    [getCanLoadCompletedLessons, getCompletedLessonsLoadingStatus],
    (canLoadMoreLessons, loadingStatus) =>
        canLoadMoreLessons && loadingStatus !== LOADING_STATUSES.LOADING && loadingStatus !== LOADING_STATUSES.ERROR
);

export const getCompletedLessonsSortKey = createSelector([getCompletedLessonsData], (data) => data.sortKey || null);

export const getActiveLessonsData = createSelector([courseViewSelector, getCourseIdFromProps], (state, courseId) => {
    if (!courseId) {
        throw Error('no courseId provided');
    }
    return state.activeLessons[courseId] || ({} as LessonsData);
});

export const getActiveLessonsLoadingStatus = createSelector(
    [getActiveLessonsData],
    (data) => data.loadingStatus || LOADING_STATUSES.UNSENT
);

export const getActiveLessons = createSelector([getActiveLessonsData], (data) => data.lessons || []);

export const getIsActiveLessonsLoading = createSelector(
    [getActiveLessonsLoadingStatus, getActiveLessons],
    (status, lessons) => status === LOADING_STATUSES.LOADING && lessons.length === 0
);

const getCanLoadActiveLessons = createSelector([getActiveLessonsData], (data) =>
    isEmpty(data) ? true : data.canLoadMoreLessons
);

export const getShouldLoadActiveLessons = createSelector(
    [getCanLoadActiveLessons, getActiveLessonsLoadingStatus],
    (canLoadMoreLessons, loadingStatus) =>
        canLoadMoreLessons && loadingStatus !== LOADING_STATUSES.LOADING && loadingStatus !== LOADING_STATUSES.ERROR
);

export const getActiveLessonsSortKey = createSelector([getActiveLessonsData], (data) => data.sortKey || null);

export const getAssignedLessonsData = createSelector([courseViewSelector, getCourseIdFromProps], (state, courseId) => {
    if (!courseId) {
        throw Error('no courseId provided');
    }
    return state.assignedLessons[courseId] || ({} as LessonsData);
});

export const getAssignedLessons = createSelector([getAssignedLessonsData], (data) => data.lessons || []);

export const getAssignedLessonsLoadingStatus = createSelector(
    [getAssignedLessonsData],
    (data) => data.loadingStatus || LOADING_STATUSES.UNSENT
);

export const getIsAssignedLessonsLoading = createSelector(
    [getAssignedLessonsLoadingStatus, getAssignedLessons],
    (status, lessons) => status === LOADING_STATUSES.LOADING && lessons.length === 0
);

const getCanLoadAssignedLessons = createSelector(getAssignedLessonsData, (data) =>
    isEmpty(data) ? true : data.canLoadMoreLessons
);

export const getShouldLoadAssignedLessons = createSelector(
    [getCanLoadAssignedLessons, getAssignedLessonsLoadingStatus],
    (canLoadMoreLessons, loadingStatus) =>
        canLoadMoreLessons && loadingStatus !== LOADING_STATUSES.LOADING && loadingStatus !== LOADING_STATUSES.ERROR
);

export const getAssignedLessonsSortKey = createSelector(getAssignedLessonsData, (data) => data.sortKey || null);

export const getShouldShowEmptyState = createSelector(
    [courseSelectors.getAllLessonsCounts, courseSelectors.getIsLessonCountsLoading],
    (lessonsCounts, isLoading) => {
        return (
            !isLoading &&
            Object.values(lessonsCounts)
                .filter(Boolean)
                .every(({ count }: CourseLessonCounts) => {
                    const allLessonsCount = count.assigned! + count.active! + count.finished! + count.unassigned!;

                    return allLessonsCount === 0;
                })
        );
    }
);
